"use client";

// This component uses Radix UI Tabs primitive which requires client-side
// interactivity (React context and state management for tab switching)

import { cva } from "class-variance-authority";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Container } from "../../elements/container/container";
import {
  TeaserBanner,
  type TeaserBannerProps,
} from "../../elements/teaser-banner/teaser-banner";
import { Text } from "../../elements/text/text";
import { TextD2C } from "../../elements/text-d2c/text";
import { Column, Container as LayoutContainer, Row } from "../../layout/grid";
import type { D2CTextColor } from "../../lib/d2c-colors";
import { cn } from "../../lib/utils";
import {
  CardGeneric,
  type CardGenericProps,
} from "../../patterns/card-generic/card-generic";
import {
  TabsContent,
  TabsList,
  Tabs as TabsPatterns,
  TabsTrigger,
} from "../../patterns/tabs/tabs";
import { TabContainerContext } from "./tab-container-context";

export interface TabsProps {
  title: string;
  subtitle: string;
  description?: string;
  tabs: Array<{
    id: string;
    label: string;
    items: Array<
      | { type: "card"; data: CardGenericProps }
      | { type: "banner"; data: TeaserBannerProps }
    >;
  }>;
  defaultTab?: string;
  onTabChange?: (tabId: string) => void;
}

export function Tabs(props: Readonly<TabsProps>) {
  return (
    <Container as="section">
      {/* Header Section */}
      <div className="mb-2">
        <Text variant="h2">{props.title}</Text>
        <Text variant="body-md">{props.subtitle}</Text>
        {props.description && (
          <Text variant="body-md">{props.description}</Text>
        )}
      </div>

      {/* Tabs Navigation */}
      <TabsPatterns
        defaultValue={props.defaultTab || props.tabs[0]?.id}
        onValueChange={props.onTabChange}
      >
        <div className="mb-8 flex">
          <TabsList>
            {props.tabs.map((tab) => (
              <TabsTrigger key={tab.id} value={tab.id}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* Content */}
        {props.tabs.map((tab) => (
          <TabsContent className="mt-8" key={tab.id} value={tab.id}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {tab.items.map((item, index) => (
                <div
                  className={
                    item.type === "banner" ? "col-span-full" : "col-span-1"
                  }
                  key={`${tab.id}-${item.type}-${index}`}
                >
                  {item.type === "banner" ? (
                    <TeaserBanner {...item.data} fluid={false} />
                  ) : (
                    <CardGeneric {...item.data} />
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        ))}
      </TabsPatterns>
    </Container>
  );
}

export interface TabContainerItemData {
  id: string;
  title: string;
  children: ReactNode;
  inspectorProps?: Record<string, string>;
}

export interface TabContainerProps {
  sys?: { id: string };
  mode: "D2C";
  title?: string;
  items: TabContainerItemData[];
  color?: D2CTextColor;
  columnWidth?: 8 | 10 | 12;
  paddingTop?: "none" | "small" | "medium" | "large";
  paddingBottom?: "none" | "small" | "medium" | "large";
  showNavigationArrows?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
}

export const tabContainerVariants = cva("", {
  variants: {
    columnWidth: {
      8: "col-span-10 col-start-2 md:col-span-8 md:col-start-3",
      10: "col-span-10 col-start-2",
      12: "col-span-12",
    },
  },
  defaultVariants: {
    columnWidth: 12,
  },
});

export function TabContainer(props: Readonly<TabContainerProps>) {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeTab = props.items[activeIndex]?.id;
  const hasNavigation = Boolean(
    props.showNavigationArrows && props.items.length > 1
  );

  const columnClasses = tabContainerVariants({
    columnWidth: props.columnWidth,
  });

  const handlePrev = () => {
    setActiveIndex((i) => (i - 1 + props.items.length) % props.items.length);
    props.onPrev?.();
  };

  const handleNext = () => {
    setActiveIndex((i) => (i + 1) % props.items.length);
    props.onNext?.();
  };

  const handleTabChange = (value: string) => {
    const index = props.items.findIndex((item) => item.id === value);
    if (index !== -1) {
      setActiveIndex(index);
    }
  };

  const prevNextButtons = (params: {
    ariaLabel: string;
    control: "prev" | "next";
    onClick: () => void;
    icon: ReactNode;
  }) => (
    <button
      aria-label={params.ariaLabel}
      className="flex aspect-square h-full cursor-pointer items-center justify-center rounded-md border border-current/20 transition-colors hover:border-current/40"
      data-carousel-control={params.control}
      onClick={params.onClick}
      type="button"
    >
      {params.icon}
    </button>
  );

  return (
    <LayoutContainer
      className="component-tab-container bg-whitesmoke"
      fullWidth={true}
      outerContainerOnly={true}
      paddingBottom={props.paddingBottom}
      paddingTop={props.paddingTop}
      testId="tab-container"
    >
      <Row className="gap-0">
        <Column className={columnClasses}>
          <TabsPatterns onValueChange={handleTabChange} value={activeTab}>
            {/* Mobile header: title + scrollable tabs */}
            <div className="flex flex-col gap-2 py-4 pl-3 md:hidden">
              {props.title && (
                <TextD2C style="no-mb" variant="h4">
                  {props.title}
                </TextD2C>
              )}

              <div className="relative">
                <div className="overflow-x-auto [&::-webkit-scrollbar]:hidden">
                  <TabsList className="w-max min-w-full gap-1 rounded-md border-none bg-white p-2">
                    {props.items.map((item) => (
                      <TabsTrigger
                        className="flex-1 cursor-pointer rounded-md border-none px-4 py-2.5 font-bold text-xs data-[state=active]:bg-whitesmoke data-[state=inactive]:bg-transparent data-[state=inactive]:text-current data-[state=active]:shadow-none"
                        data-carousel-control="tab"
                        data-tab-name={item.title}
                        key={item.id}
                        value={item.id}
                      >
                        {item.title}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>
                <div className="pointer-events-none absolute inset-y-0 right-0 w-16 rounded-r-md bg-gradient-to-r from-white/20 to-white" />
              </div>
            </div>

            {/* Desktop header: title | tabs (centered) | navigation */}
            <div className="hidden grid-cols-[1fr_auto_1fr] items-center gap-6 px-6 py-6 md:grid lg:px-8 xl:px-12">
              {props.title && (
                <TextD2C style="no-mb" variant="h3">
                  {props.title}
                </TextD2C>
              )}

              <TabsList className="w-auto gap-1 rounded-md border-none bg-white p-2">
                {props.items.map((item) => (
                  <TabsTrigger
                    className="cursor-pointer rounded-md border-none px-6 py-3 font-bold text-xs data-[state=active]:bg-whitesmoke data-[state=inactive]:bg-transparent data-[state=inactive]:text-current data-[state=active]:shadow-none"
                    data-carousel-control="tab"
                    data-tab-name={item.title}
                    key={item.id}
                    value={item.id}
                    {...item.inspectorProps}
                  >
                    {item.title}
                  </TabsTrigger>
                ))}
              </TabsList>

              <div
                className={cn(
                  "flex items-center justify-end gap-2 self-stretch p-2 pr-0",
                  !hasNavigation && "invisible"
                )}
              >
                {prevNextButtons({
                  ariaLabel: "Previous",
                  control: "prev",
                  onClick: handlePrev,
                  icon: <ChevronLeft className="h-5 w-5" />,
                })}

                {prevNextButtons({
                  ariaLabel: "Next",
                  control: "next",
                  onClick: handleNext,
                  icon: <ChevronRight className="h-5 w-5" />,
                })}
              </div>
            </div>

            {props.items.map((item) => (
              <TabsContent
                className="mt-0 overflow-hidden pb-4 md:pb-8"
                key={item.id}
                value={item.id}
              >
                <TabContainerContext.Provider value={true}>
                  {item.children}
                </TabContainerContext.Provider>
              </TabsContent>
            ))}
          </TabsPatterns>
        </Column>
      </Row>
    </LayoutContainer>
  );
}
